/**
 * Константы ключа одобрения. §Ключ 1.4, §Ключ 7.2.
 *
 * SLOT_DEADLINE_S = 300 — прямое следствие решения «ретраев нет»: публикация это единая
 * операция по одному ключу, одна попытка. Число выведено из бюджета одной попытки
 * (тик 15 + claims 5 + media 10 + проверки 2 + attempts 3 + отправка 120 + один перезапуск
 * юнита 25 = 180, округление до целых минут = 300), а не из редакционных соображений.
 *
 * Формулировка наружу: пост выходит в течение пяти минут после назначенной минуты
 * либо не выходит вовсе.
 */

/**
 * Действие ключа. `preview` (19.09.2026, docs/release-2026-09-19.md §9.1) — отправка версии в
 * ТЕСТОВЫЙ канал бренда кнопкой «Запостить в тестовый канал», в обход одобрения и слота. Это
 * отдельное подписанное действие, а не `publish` в тестовый канал: публикация потребляет
 * черновик (версия уходит в `published`, реестр публикатора пишет `draft_published`, ключ
 * вытесняет прежние по `key_seq`), а просьба обратная — посмотреть пост в тестовом канале и
 * потом утвердить ЕГО ЖЕ в боевой. Демон подписи выдаёт `preview` только на каналы из своего
 * `preview-targets.json`, публикатор отправляет его только в канал режима `test` и ведёт
 * для него свой реестр (`preview_used`). Коды не переиспользуются никогда: код входит в
 * подписываемые байты.
 */
export const ACTION_CODE = { publish: 1, revoke: 2, preview: 3 } as const;
export type ApprovalAction = keyof typeof ACTION_CODE;

export const ACTION_BY_CODE: Readonly<Record<number, ApprovalAction>> = {
  1: 'publish',
  2: 'revoke',
  3: 'preview',
};

/** deadline_ts − start_ts. Проверяется на РАВЕНСТВО и демоном подписи, и публикатором. */
export const SLOT_DEADLINE_S = 300;

/** 30 суток от выдачи до deadline_ts. Ограничивает глубину предодобрения, не окно попытки. */
export const MAX_KEY_LIFETIME_S = 2_592_000;

export const CLOCK_SKEW_S = 60;

export const MAX_PAYLOAD_BYTES = 4096;

/** Лимиты на длину строковых полей payload — проверяются ДО выделения памяти (§Ключ 1.6 п.4). */
export const FIELD_MAX_BYTES = {
  kid: 16,
  brand: 32,
  draft_id: 36,
  publication_id: 36,
  adapter: 16,
  chat_id: 20,
  approver: 64,
} as const;

/** Регулярки полей payload. §Ключ 1.3. */
export const KID_RE = /^[a-z0-9]{1,16}$/;
export const BRAND_RE = /^[a-z][a-z0-9_-]{1,31}$/;
export const CHAT_ID_RE = /^-100\d{7,13}$/;
/**
 * Логин человека, попадающий в подписанный payload. Точка, `@` и `+` разрешены с
 * 26.08.2026: логины людей — рабочие адреса (`i.baikov@strategai.ru`), а прежний класс
 * символов отвергал их на шаге подписи, то есть одобрение доходило до демона и падало
 * с BAD_REQUEST_SHAPE — нашёл шлюз фазы 1, а не человек в проде.
 *
 * Расширение безопасно ровно потому, что `approver` нигде не служит разделителем:
 * payload пишется длиной-с-префиксом (`ByteWriter.str`), а сам токен — base64url, где
 * точка отделяет ТРИ поля токена, а не поля payload. Регистр по-прежнему только нижний,
 * пробелы и управляющие символы по-прежнему отвергаются, потолок — 64 байта.
 */
export const APPROVER_RE = /^[a-z0-9_.@+-]{1,64}$/;

/**
 * Подписант ключа, выпущенного АВТООДОБРЕНИЕМ (решение Ильи 2026-10-25, docs/ai-lab.md), —
 * значение поля `approver` в подписанном payload вместо логина человека.
 *
 * Однозначность держится на двух регулярках сразу: `APPROVER_RE` подчёркивание в начале
 * допускает, а логин человека (`ACCOUNT_LOGIN_RE` в `api/accounts.ts`, `^[a-z0-9]…`) с него
 * начаться не может. Значит, `_auto` в подписанных байтах не совпадёт ни с одним человеком —
 * ни сегодняшним, ни заведённым завтра, — и демон подписи с публикатором отличают
 * автоодобренный ключ по самому ключу, не спрашивая бэкенд. Это их замки 2 и 3: каждый
 * сверяет бренд такого ключа со своим root-owned allowlist'ом и отказывает
 * `AUTO_APPROVAL_NOT_ALLOWED`, если бренда там нет.
 */
export const AUTO_APPROVER = '_auto';

/** Ключ выпущен автоодобрением, а не человеком. Сравнение строгое: префиксов и регистра нет. */
export function isAutoApprover(approver: string): boolean {
  return approver === AUTO_APPROVER;
}

export const CONTENT_HASH_RE = /^[0-9a-f]{64}$/;

/** 16 случайных байт в нижнем регистре hex. Единственная форма nonce во всей системе. */
export const NONCE_RE = /^[0-9a-f]{32}$/;

/** freshness.max_age_s: от часа до года. */
export const FRESHNESS_MIN_AGE_S = 3600;
export const FRESHNESS_MAX_AGE_S = 31_536_000;
